import React from 'react';
import PropTypes from 'prop-types';
import {
  Toaster,
  toast as hotToast,
  resolveValue,
  ToastBar,
} from 'react-hot-toast';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import styles from './Toast.module.scss';

/* ────────────────────────────────────────────────────────────
 * Toast — system toast surface.
 *
 * Thin wrapper around react-hot-toast that injects the design
 * tokens so the surface matches every other primitive (radii,
 * shadow, border, type) and exposes a tokenized icon set.
 *
 * App-shell setup (once, somewhere near the router root):
 *
 *   import { ToastContainer } from 'src/components/ui';
 *   <ToastContainer />
 *
 * Callers use the exported `toast` helper:
 *
 *   import { toast } from 'src/components/ui';
 *   toast.success('Saved');
 *   toast.error('Failed to save');
 *   toast('Plain message', { icon: <Info /> });
 *
 * Motion (MOTION.md §4.8):
 *   - Enter: slide-in from top-right + fade, 220ms ease-out-quart.
 *   - Exit: fade only, 120ms.
 * ──────────────────────────────────────────────────────────── */

const ICON_BY_TYPE = {
  success: <CheckCircle2 size={18} strokeWidth={2} />,
  error: <AlertCircle size={18} strokeWidth={2} />,
  warning: <AlertTriangle size={18} strokeWidth={2} />,
  info: <Info size={18} strokeWidth={2} />,
  blank: null,
  loading: null,
};

const surfaceClassFor = (type) =>
  [styles.surface, styles[`surface_${type}`] || '']
    .filter(Boolean)
    .join(' ');

const ToastContainer = ({ position }) => (
  <Toaster
    position={position}
    gutter={8}
    containerStyle={{ zIndex: 'var(--z-toast)' }}
    toastOptions={{
      duration: 4000,
      className: '',
      // We render the toast contents ourselves via ToastBar's render prop
      // so we can attach our own className + icon to the surface.
      style: { background: 'transparent', boxShadow: 'none', padding: 0 },
    }}
  >
    {(t) => (
      <ToastBar toast={t} style={{ background: 'transparent', boxShadow: 'none', padding: 0 }}>
        {({ message }) => (
          <div
            className={surfaceClassFor(t.type)}
            role={t.type === 'error' ? 'alert' : 'status'}
          >
            <span className={styles.icon} aria-hidden="true">
              {ICON_BY_TYPE[t.type] ?? null}
            </span>
            <div className={styles.message}>{resolveValue(message, t)}</div>
            <button
              type="button"
              className={styles.close}
              onClick={() => hotToast.dismiss(t.id)}
              aria-label="Dismiss"
            >
              <X size={14} strokeWidth={1.75} />
            </button>
          </div>
        )}
      </ToastBar>
    )}
  </Toaster>
);

ToastContainer.propTypes = {
  position: PropTypes.oneOf([
    'top-left',
    'top-center',
    'top-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ]),
};

ToastContainer.defaultProps = {
  position: 'top-right',
};

// Re-export the imperative toast() API so consumers have a single
// import path: `import { ToastContainer, toast } from 'src/components/ui'`.
export const toast = hotToast;

export default ToastContainer;
